// frontend/src/components/Client2MonitoringPage.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TableSortLabel,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  InputAdornment,
  IconButton as MuiIconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import axios from 'axios';

const Client2MonitoringPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [devices, setDevices] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ip');
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [isCheckingSingle, setIsCheckingSingle] = useState(null); // ID устройства, которое проверяется
  const [isUpdatingShifts, setIsUpdatingShifts] = useState(false); // <-- НОВОЕ: Состояние для обновления смен из Такском
  const [openImportModal, setOpenImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [lastCheckTime, setLastCheckTime] = useState(null); // Время последнего запуска проверки

  // --- НОВОЕ: Состояния для фильтров ---
  const [columnFilters, setColumnFilters] = useState({});

  // Загрузка данных
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setMessage('');
    try {
      const response = await axios.get('/client2/devices/');
      setDevices(response.data);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // --- НОВОЕ: Функция для получения уникальных значений для фильтра ---
  const getUniqueValuesForFilter = (fieldId) => {
    const uniqueValues = [...new Set(devices.map(device => device[fieldId]))].filter(v => v !== null && v !== undefined);
    return uniqueValues.sort();
  };

  // --- НОВОЕ: Обработчик изменения фильтра ---
  const handleFilterChange = (fieldId, value) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      if (value && value.length > 0) {
        newFilters[fieldId] = value;
      } else {
        delete newFilters[fieldId];
      }
      return newFilters;
    });
  };

  // --- НОВОЕ: Функция для сброса фильтра ---
  const handleClearFilter = (fieldId) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldId];
      return newFilters;
    });
  };

  // --- НОВОЕ: Применение фильтров ---
  const filteredDevices = useMemo(() => {
    let result = [...devices];
    Object.keys(columnFilters).forEach(fieldId => {
      const filterValues = columnFilters[fieldId];
      if (filterValues && Array.isArray(filterValues) && filterValues.length > 0) {
        result = result.filter(device => {
          const fieldValue = device[fieldId];
          return filterValues.includes(fieldValue);
        });
      }
    });
    return result;
  }, [devices, columnFilters]);

  const sortedDevices = useMemo(() => {
    const data = [...filteredDevices];
    if (orderBy === 'is_online') {
      return data.sort((a, b) => {
        const isAsc = order === 'asc';
        return isAsc ? (a[orderBy] ? 1 : -1) : (b[orderBy] ? 1 : -1);
      });
    }
    return data.sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === 'asc' ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDevices, order, orderBy]);

  const getFieldValue = (device, fieldId) => {
    if (fieldId === 'is_online') {
      return device[fieldId] ? '✅ В сети' : '❌ Не в сети';
    }
    return device[fieldId] || 'Н/Д';
  };

  const headCells = [
    { id: 'actions', label: 'Действия', sortable: false, filterable: false },
    { id: 'ip', label: 'IP', sortable: true, filterable: true },
    { id: 'client_name', label: 'ЮЛ', sortable: true, filterable: true },
    { id: 'address', label: 'Адрес', sortable: true, filterable: true },
    { id: 'kkt_factory_number', label: 'Заводской номер ККТ', sortable: true, filterable: true },
    { id: 'is_online', label: 'В сети', sortable: true, filterable: true },
    { id: 'software_version', label: 'Версия ПО', sortable: true, filterable: true },
    { id: 'firmware_version', label: 'Версия Прошивки', sortable: true, filterable: true },
    { id: 'utm_version', label: 'Версия УТМ', sortable: true, filterable: true },
    { id: 'shifts_yesterday_count', label: 'Кол-во смен за вчера', sortable: true, filterable: true },
//    { id: 'last_checked_at', label: 'Вермя посл проверки', sortable: false, filterable: false },
  ];

  // --- ОБНОВЛЕНО: handleCheckAll теперь запускает полную проверку с обновлением смен из Такском ---
  const handleCheckAll = async () => {
    setIsCheckingAll(true); // <-- Используем старое состояние для кнопки "Обновить все"
    setMessage('');
    setError('');
    try {
      // Вызываем НОВЫЙ эндпоинт, объединяющий все проверки
      const response = await axios.post('/client2/check_all_with_shifts_from_taxcom/'); // <-- НОВЫЙ ЭНДПОИНТ
      setMessage(response.data.message || 'Полная проверка всех устройств завершена.');
      setLastCheckTime(new Date().toLocaleTimeString()); // Обновляем время последнего запуска
      fetchData(); // Обновить таблицу после выполнения
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при выполнении полной проверки');
    } finally {
      setIsCheckingAll(false); // <-- Используем старое состояние
    }
  };
  // --- КОНЕЦ ОБНОВЛЕНИЯ ---

  // --- НОВОЕ: handleUpdateShiftsFromTaxcom ---
  const handleUpdateShiftsFromTaxcom = async () => {
    setIsUpdatingShifts(true); // <-- Используем новое состояние
    setMessage('');
    setError('');
    try {
      const response = await axios.post('/client2/update-shifts-from-taxcom/'); // <-- СТАРЫЙ ЭНДПОИНТ (только смены)
      setMessage(response.data.message || 'Обновление кол-ва смен завершено.');
      setLastCheckTime(new Date().toLocaleTimeString()); // Обновляем время последнего запуска
      fetchData(); // Обновить таблицу после выполнения
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при обновлении кол-ва смен');
    } finally {
      setIsUpdatingShifts(false); // <-- Используем новое состояние
    }
  };
  // --- КОНЕЦ НОВОГО ---

  // Обновить одно устройство (старая логика, если нужна)
  const handleCheckSingle = async (deviceId) => {
    setIsCheckingSingle(deviceId);
    setMessage('');
    setError('');
    try {
      const response = await axios.post(`/client2/check_device/${deviceId}/`);
      setMessage(response.data.message || `Проверка устройства ${deviceId} запущена.`);
    } catch (err) {
      setError(err.response?.data?.detail || `Ошибка при проверке устройства ${deviceId}`);
    } finally {
      setIsCheckingSingle(null);
    }
  };

  // Обработчики для импорта
  const handleImportClick = () => {
    setOpenImportModal(true);
    setImportFile(null);
    setImportError('');
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
    setImportError('');
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setImportError('Пожалуйста, выберите файл');
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    setImportLoading(true);
    setImportError('');

    try {
      const response = await axios.post('/api/client2/import_devices_from_excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage(response.data.message || 'Импорт успешно завершён');
      setOpenImportModal(false);
      fetchData(); // Обновить таблицу
    } catch (err) {
      setImportError(err.response?.data?.detail || 'Ошибка при импорте');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportClose = () => {
    setOpenImportModal(false);
    setImportFile(null);
    setImportError('');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
          Главпиво
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleImportClick}
            sx={{ mr: 2 }}
          >
            Импорт XLS
          </Button>
          {/* --- ОБНОВЛЕНО: Текст кнопки и состояние --- */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckAll}
            disabled={isCheckingAll} // <-- Используем старое состояние
            startIcon={isCheckingAll ? <CircularProgress size={20} /> : null} // <-- Иконка загрузки
            sx={{ mr: 2 }} // <-- Добавим отступ справа
          >
            {isCheckingAll ? 'Обновление...' : 'Обновить все'} {/* <-- Возвращаем старый текст */}
          </Button>
          {/* --- КОНЕЦ ОБНОВЛЕНИЯ --- */}
          {/* --- НОВОЕ: Кнопка для обновления ТОЛЬКО смен из Такском --- */}
        </Box>
      </Box>

      {/* Сообщение о времени последнего запуска проверки */}
      {lastCheckTime && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Последняя проверка запущена: {lastCheckTime}
        </Alert>
      )}

      {/* --- НОВОЕ: Блок фильтров --- */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {headCells.filter(cell => cell.filterable).map((headCell) => (
          <FormControl key={headCell.id} size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{headCell.label}</InputLabel>
            <Select
              multiple
              value={columnFilters[headCell.id] || []}
              onChange={(e) => handleFilterChange(headCell.id, e.target.value)}
              input={<OutlinedInput label={headCell.label} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      onDelete={() => {
                        const newValues = columnFilters[headCell.id].filter(v => v !== value);
                        handleFilterChange(headCell.id, newValues);
                      }}
                      deleteIcon={
                        <MuiIconButton
                          onMouseDown={(event) => event.preventDefault()}
                          size="small"
                        >
                          <CloseIcon />
                        </MuiIconButton>
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {getUniqueValuesForFilter(headCell.id).map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            {columnFilters[headCell.id] && columnFilters[headCell.id].length > 0 && (
              <MuiIconButton
                size="small"
                onClick={() => handleClearFilter(headCell.id)}
                sx={{ position: 'absolute', right: 28, top: 28 }}
              >
                <CloseIcon fontSize="small" />
              </MuiIconButton>
            )}
          </FormControl>
        ))}
      </Box>

      {loading && <CircularProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table stickyHeader aria-label="client2 monitoring table">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sx={{
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    minWidth: headCell.id === 'client_name' ? 180 : 'auto',
                  }}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDevices.map((device) => (
              <TableRow key={device.id}>
                <TableCell>
                  <Tooltip title="Редактировать">
                    <IconButton size="small" color="primary">
                      ✏️
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить">
                    <IconButton size="small" color="error">
                      🗑️
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Обновить (SSH)">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleCheckSingle(device.id)}
                      disabled={isCheckingSingle === device.id}
                    >
                      {isCheckingSingle === device.id ? <CircularProgress size={20} /> : '🔄'}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                {headCells.slice(1).map((headCell) => (
                  <TableCell key={headCell.id} sx={{ py: 1 }}>
                    {getFieldValue(device, headCell.id)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Модальное окно импорта */}
      <Dialog open={openImportModal} onClose={handleImportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт устройств клиента 2</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Выберите XLSX-файл с устройствами для импорта.
          </DialogContentText>
          <input
            accept=".xlsx,.xls"
            type="file"
            onChange={handleFileChange}
            id="upload-file"
            style={{ display: 'none' }}
          />
          <label htmlFor="upload-file">
            <Button variant="contained" component="span" sx={{ mt: 2 }}>
              Выбрать файл
            </Button>
          </label>
          {importFile && <p>{importFile.name}</p>}
          {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Отмена</Button>
          <Button onClick={handleImportSubmit} variant="contained" disabled={importLoading}>
            {importLoading ? <CircularProgress size={24} /> : 'Импорт'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Client2MonitoringPage;